import { useRef, useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Heading } from "../../";
import { SectionBadge } from "pages/AIHumanity/index";

gsap.registerPlugin(ScrollTrigger);

// Ring geometry
const R = 80;
const CIRC = 2 * Math.PI * R; // ≈ 502.65

const steps = [
  { heading: "Understands emotion + behavior." },
  { heading: "Maintains persistent relationship state." },
  { heading: "Adapts behavior in real time." },
];

// Split heading into word spans for scrub-linked reveal
function WordHeading({ text, className, style }) {
  const words = text.split(" ");
  return (
    <Heading size="2xl" as="h2" className={className} style={style}>
      {words.map((word, i) => (
        <span
          key={i}
          className="sol-word"
          style={{ display: "inline-block", marginRight: "0.28em", opacity: 0.15, willChange: "opacity, transform" }}
        >
          {word}
        </span>
      ))}
    </Heading>
  );
}

function GsapSection1a() {
  const containerRef = useRef(null);
  const ringRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const panels = gsap.utils.toArray(".sol-panel");
      const dots = gsap.utils.toArray(".sol-dot");

      // ── Ring setup ──────────────────────────────────────────────
      gsap.set(ringRef.current, {
        strokeDasharray: CIRC,
        strokeDashoffset: CIRC,
      });

      // ── Initial panel + dot states ───────────────────────────────
      gsap.set(panels[0], { autoAlpha: 1, y: 0 });
      gsap.set(panels.slice(1), { autoAlpha: 0, y: 30 });
      gsap.set(dots[0], { scale: 1.6, opacity: 1 });
      gsap.set(dots.slice(1), { scale: 1, opacity: 0.3 });

      // ── Main timeline ────────────────────────────────────────────
      // completed: true after user has scrolled fully past this section.
      // onEnterBack skips the rewind by jumping scroll above the section start.
      let completed = false;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "+=260%",
          pin: true,
          scrub: 1.5,
          pinSpacing: true,
          onEnter: () => { completed = false; },
          onLeave: () => { completed = true; },
          onEnterBack: (self) => {
            if (completed) {
              self.scroll(self.start - 2);
            }
          },
        },
      });

      // Helper: get word elements for a panel
      const words = (panel) => panel.querySelectorAll(".sol-word");

      // ── Phase 1: words reveal + ring fills to 1/3 ───────────────
      tl.to(words(panels[0]), {
        opacity: 1,
        y: 0,
        stagger: { each: 1.8 },
        duration: 2.5,
        ease: "power2.out",
      });
      tl.to(ringRef.current, {
        strokeDashoffset: CIRC * 0.67,
        duration: 12,
        ease: "power2.inOut",
      }, "<");

      // ── Transition 1 → 2 ────────────────────────────────────────
      tl.to(panels[0], { autoAlpha: 0, y: -28, duration: 6, ease: "power2.in" });
      tl.to(dots[0], { scale: 1, opacity: 0.3, duration: 4 }, "<+=2");
      tl.fromTo(
        panels[1],
        { autoAlpha: 0, y: 28 },
        { autoAlpha: 1, y: 0, duration: 6, ease: "power2.out" },
        "-=4"
      );
      tl.to(dots[1], { scale: 1.6, opacity: 1, duration: 4 }, "<");

      // ── Phase 2: words reveal + ring fills to 2/3 ───────────────
      tl.to(words(panels[1]), {
        opacity: 1,
        y: 0,
        stagger: { each: 1.8 },
        duration: 2.5,
        ease: "power2.out",
      });
      tl.to(ringRef.current, {
        strokeDashoffset: CIRC * 0.33,
        duration: 12,
        ease: "power2.inOut",
      }, "<");

      // ── Transition 2 → 3 ────────────────────────────────────────
      tl.to(panels[1], { autoAlpha: 0, y: -28, duration: 6, ease: "power2.in" });
      tl.to(dots[1], { scale: 1, opacity: 0.3, duration: 4 }, "<+=2");
      tl.fromTo(
        panels[2],
        { autoAlpha: 0, y: 28 },
        { autoAlpha: 1, y: 0, duration: 6, ease: "power2.out" },
        "-=4"
      );
      tl.to(dots[2], { scale: 1.6, opacity: 1, duration: 4 }, "<");

      // ── Phase 3: words reveal + ring completes ───────────────────
      tl.to(words(panels[2]), {
        opacity: 1,
        y: 0,
        stagger: { each: 1.8 },
        duration: 2.5,
        ease: "power2.out",
      });
      tl.to(ringRef.current, {
        strokeDashoffset: 0,
        duration: 14,
        ease: "power2.inOut",
      }, "<");

      // Hold at the end so step 3 lingers before un-pinning
      tl.to({}, { duration: 8 });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ height: "100vh", overflow: "hidden" }}
      className="bg-black-900_02"
    >
      <div className="sol-layout">

        {/* ── Left column ─────────────────────────────────────────── */}
        <div className="sol-col-left">

          {/* Badge row */}
          <div className="sol-badge-row">
            <SectionBadge>The Solution</SectionBadge>
          </div>

          {/* Stacked text panels */}
          <div className="sol-panels-wrap">
            {steps.map((s, i) => (
              <div
                key={i}
                className="sol-panel"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0,
                  visibility: "hidden",
                }}
              >
                <WordHeading
                  text={s.heading}
                  className="!text-gray-300 leading-[120%]"
                  style={{ lineHeight: "1.2" }}
                />
              </div>
            ))}
          </div>

          {/* Step dots */}
          <div className="sol-dots">
            {steps.map((_, i) => (
              <div
                key={i}
                className="sol-dot"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: i === 0 ? "#6ddcff" : "rgba(255,255,255,0.35)",
                  flexShrink: 0,
                }}
              />
            ))}
          </div>

        </div>

        {/* ── Right column: progress ring ──────────────────────────── */}
        <div className="sol-col-right">
          <svg
            width="340"
            height="340"
            viewBox="0 0 200 200"
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: "rotate(-90deg)", overflow: "visible" }}
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="solRingGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#6ddcff" />
                <stop offset="100%" stopColor="#7e60f8" />
              </linearGradient>
            </defs>

            {/* Faint track ring */}
            <circle
              cx="100"
              cy="100"
              r={R}
              fill="none"
              stroke="rgba(255,255,255,0.07)"
              strokeWidth="6"
            />

            {/* Animated progress ring */}
            <circle
              ref={ringRef}
              cx="100"
              cy="100"
              r={R}
              fill="none"
              stroke="url(#solRingGrad)"
              strokeWidth="6"
              strokeLinecap="round"
            />
          </svg>
        </div>

      </div>
    </div>
  );
}

export default GsapSection1a;
